import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { io, Socket } from 'socket.io-client';
import { Notification, NotificationPreferences, PaginatedNotifications } from '../types/notification';
import { apiClient } from '../services/apiClient';

interface NotificationState {
    notifications: Notification[];
    unreadCount: number;
    totalCount: number;
    page: number;
    hasMore: boolean;
    loading: boolean;
    error: string | null;
    preferences: NotificationPreferences | null;
    socket: Socket | null;
    isConnected: boolean;
}

interface NotificationActions {
    connect: (token?: string) => void;
    disconnect: () => void;
    fetchNotifications: (page?: number, limit?: number) => Promise<void>;
    fetchUnreadCount: () => Promise<void>;
    markAsRead: (notificationId: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    dismiss: (notificationId: string) => Promise<void>;
    fetchPreferences: () => Promise<void>;
    updatePreferences: (preferences: Partial<NotificationPreferences>) => Promise<void>;
    addNotification: (notification: Notification) => void;
    setUnreadCount: (count: number) => void;
}

type NotificationStore = NotificationState & NotificationActions;

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export const useNotificationStore = create<NotificationStore>()(
    devtools(
        (set, get) => ({
            // State
            notifications: [],
            unreadCount: 0,
            totalCount: 0,
            page: 1,
            hasMore: true,
            loading: false,
            error: null,
            preferences: null,
            socket: null,
            isConnected: false,

            // Actions
            connect: (token?: string) => {
                const { socket } = get();
                if (socket?.connected) return;

                // Get token from parameter or localStorage
                const authToken = token || localStorage.getItem('accessToken');
                if (!authToken) {
                    console.warn('No auth token available for WebSocket connection');
                    return;
                }

                const newSocket = io(`${API_BASE_URL}/notifications`, {
                    auth: { token: authToken },
                    transports: ['websocket'],
                });

                newSocket.on('connect', () => {
                    set({ isConnected: true });
                });

                newSocket.on('disconnect', () => {
                    set({ isConnected: false });
                });

                newSocket.on('notification', (notification: Notification) => {
                    get().addNotification(notification);
                });

                newSocket.on('unread_count', (data: { count: number }) => {
                    set({ unreadCount: data.count });
                });

                newSocket.on('connect_error', (error) => {
                    console.error('Notification socket error:', error);
                    set({ error: 'Failed to connect to notifications' });
                });

                set({ socket: newSocket });
            },

            disconnect: () => {
                const { socket } = get();
                if (socket) {
                    socket.disconnect();
                    set({ socket: null, isConnected: false });
                }
            },

            fetchNotifications: async (page = 1, limit = 20) => {
                set({ loading: true, error: null });
                try {
                    const response = await apiClient.get<PaginatedNotifications>(
                        `/notifications`,
                        { params: { page, limit } }
                    );

                    const { items, total, totalPages } = response.data;

                    if (page === 1) {
                        set({
                            notifications: items,
                            totalCount: total,
                            page,
                            hasMore: page < totalPages,
                            loading: false,
                        });
                    } else {
                        set((state) => ({
                            notifications: [...state.notifications, ...items],
                            totalCount: total,
                            page,
                            hasMore: page < totalPages,
                            loading: false,
                        }));
                    }
                } catch (error: any) {
                    set({
                        loading: false,
                        error: error.response?.data?.message || 'Failed to fetch notifications',
                    });
                }
            },

            fetchUnreadCount: async () => {
                try {
                    const response = await apiClient.get<{ count: number }>(
                        `/notifications/unread-count`
                    );
                    set({ unreadCount: response.data.count });
                } catch (error) {
                    console.error('Failed to fetch unread count:', error);
                }
            },

            markAsRead: async (notificationId: string) => {
                try {
                    await apiClient.patch(`/notifications/${notificationId}/read`);

                    set((state) => ({
                        notifications: state.notifications.map((n) =>
                            n.id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
                        ),
                        unreadCount: Math.max(0, state.unreadCount - 1),
                    }));

                    // Also notify via WebSocket
                    const { socket } = get();
                    socket?.emit('markAsRead', { notificationId });
                } catch (error: any) {
                    set({ error: error.response?.data?.message || 'Failed to mark as read' });
                }
            },

            markAllAsRead: async () => {
                try {
                    await apiClient.post(`/notifications/read-all`);

                    set((state) => ({
                        notifications: state.notifications.map((n) => ({
                            ...n,
                            isRead: true,
                            readAt: new Date().toISOString(),
                        })),
                        unreadCount: 0,
                    }));

                    // Also notify via WebSocket
                    const { socket } = get();
                    socket?.emit('markAllAsRead');
                } catch (error: any) {
                    set({ error: error.response?.data?.message || 'Failed to mark all as read' });
                }
            },

            dismiss: async (notificationId: string) => {
                try {
                    await apiClient.delete(`/notifications/${notificationId}`);

                    set((state) => {
                        const notification = state.notifications.find((n) => n.id === notificationId);
                        return {
                            notifications: state.notifications.filter((n) => n.id !== notificationId),
                            unreadCount: notification && !notification.isRead
                                ? Math.max(0, state.unreadCount - 1)
                                : state.unreadCount,
                        };
                    });
                } catch (error: any) {
                    set({ error: error.response?.data?.message || 'Failed to dismiss notification' });
                }
            },

            fetchPreferences: async () => {
                try {
                    const response = await apiClient.get<NotificationPreferences>(
                        `/notifications/preferences`
                    );
                    set({ preferences: response.data });
                } catch (error: any) {
                    console.error('Failed to fetch preferences:', error);
                }
            },

            updatePreferences: async (preferences: Partial<NotificationPreferences>) => {
                try {
                    const response = await apiClient.patch<NotificationPreferences>(
                        `/notifications/preferences`,
                        preferences
                    );
                    set({ preferences: response.data });
                } catch (error: any) {
                    set({ error: error.response?.data?.message || 'Failed to update preferences' });
                }
            },

            addNotification: (notification: Notification) => {
                set((state) => ({
                    notifications: [notification, ...state.notifications],
                    unreadCount: state.unreadCount + 1,
                    totalCount: state.totalCount + 1,
                }));
            },

            setUnreadCount: (count: number) => {
                set({ unreadCount: count });
            },
        }),
        { name: 'NotificationStore' }
    )
);

// Utility hooks
export const useNotifications = () => useNotificationStore((state) => state.notifications);
export const useUnreadCount = () => useNotificationStore((state) => state.unreadCount);
export const useNotificationActions = () => {
    const {
        connect,
        disconnect,
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        dismiss,
    } = useNotificationStore();
    return { connect, disconnect, fetchNotifications, fetchUnreadCount, markAsRead, markAllAsRead, dismiss };
};
